import { FC, useMemo, useState } from "react";
import {
  InventoryDetailSingleInterface
} from "../../interfaces/InventoryInterface";
import classNames from "classnames";
import LoadingIcon from "../../components/LodingIcon";
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ExclamationTriangleIcon,
  QuestionMarkCircleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { useAppInventory, useAppSelector } from "../../store/hooks";
import Modal from "../../components/Modal";
import { PrimaryButton, SecondaryButton } from "../../components/Buttons";
import {
  removePendingDetails,
  removeProcessedDetails,
} from "../../store/slices/inventory";
import { alertService } from "../../services";
import {
  //updateDetailStatusDeleted,
  updateSingleDetailStatusDeleted,
} from "../../services/inventoryServices";
import { ManifestIncidence } from "../../interfaces/ManifestInterface";

interface InventoryDetailItemProps {
  detail: InventoryDetailSingleInterface;
  index: number;
  detailsBeingValidated: string[];
  onRetry: () => Promise<void>;
}
const InventoryDetailItem: FC<InventoryDetailItemProps> = ({
  detail,
  index,
  detailsBeingValidated,
  onRetry,
}) => {
  const inventory = useAppInventory();
  const user = useAppSelector((state) => state.user);
  // const pendingDetails = useAppSelector(
  //   (state) => state.inventory.pendingDetails
  // );
  const processedDetails = useAppSelector(
    (state) => state.inventory.processedDetails
  );
  // const inventoryHeader = useAppSelector(
  //   (state) => state.inventory.inventoryHeader
  // );

  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const processedDetailList = useMemo(() => {
    return Object.keys(processedDetails).map((id) => processedDetails[id]);
  }, [processedDetails]);

  const areMissingDetailsWarning = useMemo(() => {
    const shipmentNumber = detail.shipmentNumber;
    const totalPieces = detail.totalPieces;
    const pieces = new Set(
      new Array(totalPieces).fill(null).map((_, i) => i + 1)
    );

    processedDetailList
      .filter((detail) => detail.shipmentNumber === shipmentNumber)
      .forEach((detail) => {
        const pieceNumber = detail.pieceNumber;

        pieces.delete(pieceNumber);
      });

    if (pieces.size > 0) {
      const missingDetails = Array.from(pieces)
        .map((piece) => `${piece}/${totalPieces}`)
        .join(", ");
      return `La guía ${shipmentNumber} tiene piezas faltantes: ${missingDetails}`;
    } else {
      return "";
    }
  }, [detail, processedDetailList]);

  const reloadable = useMemo(() => {
    return (
      (detail.incidence === ManifestIncidence.CRITIC_ERROR ||
        detail.incidence === ManifestIncidence.NOT_VALIDATED) &&
      !detailsBeingValidated.some(
        (d) => d === `${detail.shipmentNumber}-${detail.pieceNumber}`
      )
    );
  }, [detail, detailsBeingValidated]);

  // const handleShipmentDelete = async () => {
  //   const userLogin = user.user?.userLogin;
  //   const inventoryHeaderID = inventoryHeader?.inventoryHeaderID;

  //   if (!userLogin) {
  //     alertService.error(
  //       "No hay un nombre de usuario asociado a la sesión actual"
  //     );
  //     return;
  //   }
  //   if (!inventoryHeaderID) {
  //     alertService.error(
  //       "Parece que no hay ningún inventario cargado"
  //     );
  //     return;
  //   }

  //   const detailsToDelete = Object.keys(processedDetails)
  //     .map((id) => processedDetails[id])
  //     .concat(Object.keys(pendingDetails).map((id) => pendingDetails[id]))
  //     .filter((d) => d.shipmentNumber === detail.shipmentNumber);
  //   const inventoryDetailID = detailsToDelete.find(
  //     (d) => d.inventoryDetailID
  //   )?.inventoryDetailID;

  //   if (inventoryDetailID) {
  //     const deleteStatus = await updateDetailStatusDeleted(
  //       inventoryDetailID,
  //       userLogin,
  //       detail.shipmentNumber
  //     );

  //     if (deleteStatus.didError) {
  //       alertService.error(
  //         `Hubo un error eliminando la guía ${detail.shipmentNumber}`,
  //         deleteStatus.errorMessage
  //       );
  //       return;
  //     }
  //   }

  //   inventory(removeProcessedDetails(detailsToDelete));
  //   inventory(removePendingDetails(detailsToDelete));
  // };

  const handleDetailDelete = async () => {
    const userLogin = user.user?.userLogin;
    const inventoryDetailID = detail.inventoryDetailID;

    if (!userLogin) {
      alertService.error(
        "No hay un nombre de usuario asociado a la sesión actual"
      );
      return;
    }
    if (!inventoryDetailID) {
      inventory(removeProcessedDetails([detail]));
      inventory(removePendingDetails([detail]));
      return;
    }

    const deleteStatus = await updateSingleDetailStatusDeleted(
      inventoryDetailID,
      userLogin
    );

    if (deleteStatus.didError) {
      alertService.error(
        "Hubo un error eliminando la pieza",
        deleteStatus.errorMessage
      );
      return;
    }

    inventory(removeProcessedDetails([detail]));
    inventory(removePendingDetails([detail]));
  };
  return (
    <tr
      key={`${detail.shipmentNumber}-${detail.pieceNumber}`}
      className={classNames(
        "hover:bg-gray-100 text-center transition-opacity duration-500",
        index % 2 === 0 ? "bg-gray-50" : "bg-white"
      )}
      title={[detail.errors, areMissingDetailsWarning]
        .filter((message) => message)
        .join("\n")}
    >
      <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
        {detail.shipmentNumber}
      </td>

      <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
        {detail.buCodeSource.toUpperCase()}
      </td>

      <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
        {detail.locationDestinationCode.toUpperCase()}
      </td>

      <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
        {detail.serviceName?.toUpperCase()}
      </td>

      <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
        {detail.pieceNumber}
      </td>

      <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
        {detail.totalPieces}
      </td>

      <td className="px-4 py-4 text-xs text-gray-700 font-semibold truncate">
        {detail.chargedWeight ?? 0.00}
      </td>

      <td className="px-4 py-2 text-xs text-gray-700 font-semibold">
        <div className="flex justify-center items-center space-x-1">
          <div className="w-7 h-7">
            {detail.incidence === ManifestIncidence.NOT_VALIDATED  ? (
              <LoadingIcon size="18px" />
            ) : detail.incidence === ManifestIncidence.NO_ERROR && !areMissingDetailsWarning? (
              <CheckCircleIcon className="text-green-600 w-7 h-7" />
            ) : (detail.incidence === ManifestIncidence.WARNING  || areMissingDetailsWarning)? (
              <ExclamationTriangleIcon className="text-orange-600 w-7 h-7" />
            ) : detail.incidence === ManifestIncidence.ERROR ||
              detail.incidence === ManifestIncidence.CRITIC_ERROR ? (
              <ExclamationCircleIcon className="text-red-600 w-7 h-7" />
            ) : (
              <QuestionMarkCircleIcon className="text-red-600 w-7 h-7" />
            )}
          </div>
        </div>
      </td>

      {/* <td className="px-4 py-2 text-xs text-gray-700 font-semibold">
        <div className="flex justify-center items-center gap-2=">
          <ArrowPathIcon
            className={classNames(
              "text-blue-600 w-7 h-7",
              !reloadable && "hidden",
              reloadable && "cursor-pointer hover:animate-spin"
            )}
            title="Reintentar validación"
            onClick={onRetry}
          />

          <TrashIcon
            onClick={() => setOpenDeleteModal(true)}
            className="h-7 w-7 text-red-500 cursor-pointer hover:text-red-700"
          />
        </div>
      </td> */}

      <Modal openModal={openDeleteModal} setOpenModal={setOpenDeleteModal}>
        <div className="flex flex-col gap-4 mx-auto items-center">
          <div className="flex flex-col items-center">
            <QuestionMarkCircleIcon className="text-blue-500 h-28 w-28 mx-auto opacity-90" />
            <h2 className="text-xl font-semibold leading-7 text-gray-700 text-center mt-2">
              Eliminar pieza del inventario {detail.shipmentNumber}
            </h2>
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-gray-600 text-center whitespace-pre-line">
              ¿Está seguro que desea eliminar esta pieza?
            </p>
          </div>

          <div className="flex w-full flex-row gap-8 items-center justify-between mt-4">
            <SecondaryButton
              onClick={() => setOpenDeleteModal(false)}
              className="px-4 py-2"
            >
              Cancelar
            </SecondaryButton>

            <div className="flex items-center gap-4">
              {/* <PrimaryButton
                onClick={async () => {
                  await handleDetailDelete();
                  setOpenDeleteModal(false);
                }}
                className="px-4 py-2 truncate"
              >
                Eliminar esta pieza
              </PrimaryButton> */}

              {/* <PrimaryButton
                onClick={async () => {
                  await handleShipmentDelete();
                  setOpenDeleteModal(false);
                }}
                className="px-4 py-2 truncate"
              >
                Eliminar todas las piezas
              </PrimaryButton> */}
            </div>
          </div>
        </div>
      </Modal>
    </tr>
  );
};

export default InventoryDetailItem;